import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, ShoppingCart } from 'lucide-react';

/**
 * OverageConsentModal — shown when a resource limit is reached.
 * Displays a "limit reached" notice and a link to the billing page
 * to purchase additional slots. The embedded form is always disabled.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   resourceLabel: string — e.g. "user", "branch", "location", "asset"
 *   current: number
 *   max: number
 *   children: React.ReactNode — the form fields (always rendered disabled)
 */
export default function OverageConsentModal({
  isOpen,
  onClose,
  resourceLabel,
  current,
  max,
  children,
}) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const label = resourceLabel ?? 'item';
  const plural = `${label}s`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Plan limit reached — {current}/{max} {plural}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto">

          {/* Limit notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                You've used all {max} {label} slot{max !== 1 ? 's' : ''} included in your plan.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                To create additional {plural}, purchase extra slots from your billing page.
                Your form will be available once your plan is upgraded.
              </p>
            </div>
          </div>

          {/* Form fields — always disabled as a preview */}
          <fieldset disabled className="opacity-40 pointer-events-none select-none">
            {children}
          </fieldset>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { onClose(); navigate('/portal/billing'); }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <ShoppingCart className="w-4 h-4" />
            Buy Slots
          </button>
        </div>

      </div>
    </div>
  );
}
